import * as React from 'react';
import { PageBrowserConfiguration } from './PageBrowserConfiguration';
import { PaginationContext, PaginationContextType } from './PaginationContext';
import { PaginationKeyInterface } from './PaginationKeyInterface';

interface PageBrowserProps extends PageBrowserConfiguration {
    children?: never // The Page Browser does not accept child components
}

const defaultProps: Readonly<Partial<PageBrowserProps>> = {
    labels: {
        first: 'First',
        last: 'Last',
        previous: 'Previous',
        next: 'Next'
    }
};

export class PageBrowser<T extends PaginationKeyInterface> extends React.Component<PageBrowserProps> {
    public static contextType = PaginationContextType;
    public context!: PaginationContext<T>;

    public render() {
        const pager = this.context.pager;
        if (!pager) {
            console.error('[PageBrowser] Pager not yet initialized');

            return null;
        }

        // Don't display pager if there is only 1 page
        if (!pager.pages || pager.pages.length <= 1) {
            console.debug('[PageBrowser] Pager has no pages assigned', this.context);

            return null;
        }

        const labels = this.getLabels();
        const currentPage = pager.currentPage;

        return (
            <ul className="pagination">
                <li className={`page-item first ${currentPage === 1 ? 'disabled' : ''}`}>
                    <button className="page-link" onClick={() => this.setPage(1)}>{labels.first}</button>
                </li>
                <li className={`page-item previous ${currentPage === 1 ? 'disabled' : ''}`}>
                    <button className="page-link"
                            onClick={() => this.setPage(currentPage - 1)}>{labels.previous}</button>
                </li>
                {pager.pages.map(
                    (page, index) => {
                        const itemClassName = `page-item page-number ${currentPage === page ? 'active' : ''}`;

                        return <li key={index} className={itemClassName}>
                            <button className="page-link" onClick={() => this.setPage(page)}>{page}</button>
                        </li>;
                    }
                )}
                <li className={`page-item next ${currentPage === pager.totalPages ? 'disabled' : ''}`}>
                    <button className="page-link" onClick={() => this.setPage(currentPage + 1)}>{labels.next}</button>
                </li>
                <li className={`page-item last ${currentPage === pager.totalPages ? 'disabled' : ''}`}>
                    <button className="page-link" onClick={() => this.setPage(pager.totalPages)}>{labels.last}</button>
                </li>
            </ul>
        );
    }

    private setPage(page: number) {
        // Call change page function in parent component
        this.context.showPage(page);
    }

    private getLabels() {
        return Object.assign({}, defaultProps.labels, this.context.labels, this.props.labels);
    }
}
